import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { PieChart } from '../../../../../CommonComponents/OPL/charts/PieChart/PieChart';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { formatPercentage } from '../../../../../CommonUtils/formatNumber';
import { PortfolioDTO } from '../../../../api/useSubmitAnswersMutation';
import { MarginProps } from '../../../../../theme/styledProps';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Separator } from '../../../../../CommonComponents/OPL/Separator/Separator';
import { cashColor, chartColors } from './AssetAllocationChart.meta';
import { selectShowPortfolioProjectedReturns } from '../../../../../CommonSelectors/CommonAreaSelector';

const CHART_SIZE = 170;

interface Props extends MarginProps {
  portfolio: PortfolioDTO;
  isPrint?: boolean;
}

export const AssetAllocationChart: React.FC<Props> = ({ portfolio, isPrint, ...rest }) => {
  const { AssetAllocation } = portfolio;
  const showPortfolioProjectedReturns = useSelector(selectShowPortfolioProjectedReturns);

  const allocationWithColors = AssetAllocation.map((allocation, index) => ({
    ...allocation,
    color:
      allocation.color ||
      (allocation.name === 'Cash' ? cashColor : chartColors[index % chartColors.length]),
  }));

  return (
    <Box display="flex" alignItems="center" gridGap={56} mx="16" {...rest}>
      <Box height={CHART_SIZE} width={CHART_SIZE}>
        <PieChart
          customSettings={{
            chart: { width: CHART_SIZE, height: CHART_SIZE },
            plotOptions: {
              pie: { innerSize: '75%', borderWidth: 1 },
              series: { animation: !isPrint },
            },
            accessibility: { enabled: false },
          }}
          series={[{ data: allocationWithColors }]}
        />
      </Box>

      <Box flex={1}>
        <Box as="ul" pl="0">
          {allocationWithColors.map(({ name, y, color }) => (
            <Box
              as="li"
              key={name}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb="16"
            >
              <Box display="flex" alignItems="center">
                <Dot size={8} bg={color} mr={6} />

                <TruncatedText fontWeight={500} color="neutral.600" mr="16">
                  {name}
                </TruncatedText>
              </Box>

              <Text variant="tabular" fontWeight={500} color="neutral.600">
                {formatPercentage(y)}
              </Text>
            </Box>
          ))}
        </Box>

        {showPortfolioProjectedReturns && (
          <>
            <Separator my="16" />

            <Box display="flex" justifyContent="space-between">
              <Text fontWeight={500} color="neutral.600">
                Projected Return
              </Text>

              <Text variant="tabular" fontWeight={500} color="neutral.600">
                {portfolio.ExpectedReturn}
              </Text>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
